import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Chip,
  IconButton,
  Collapse,
  Grid,
  Button,
} from "@mui/material";
import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import ApiService from "../../services/api-service";
import "./Admin.scss";

function PaymentRow({ payment }) {
  const [open, setOpen] = useState(false);

  const getPaymentMethodDisplay = (payment) => {
    // Infer payment method from available data
    if (payment.transactionId?.startsWith("GPA")) return "Google Play";
    if (payment.productId?.includes("apple") || payment.productId?.includes("ios")) return "Apple Pay";
    if (payment.orderId?.includes("paypal")) return "PayPal";
    if (payment.orderId?.includes("moonpay")) return "MoonPay";
    if (payment.orderId?.includes("stripe")) return "Stripe";
    if (payment.paymentMethod) return payment.paymentMethod.toUpperCase();
    if (payment.deviceType === 2) return "iOS Purchase";
    if (payment.deviceType === 1) return "Android Purchase";
    return "Unknown";
  };

  const getPaymentMethodColor = (method) => {
    const methodLower = method.toLowerCase();
    if (methodLower.includes("apple")) return "#000000";
    if (methodLower.includes("google")) return "#4285F4";
    if (methodLower.includes("paypal")) return "#003087";
    if (methodLower.includes("moonpay")) return "#7B3FF2";
    if (methodLower.includes("stripe")) return "#635BFF";
    if (methodLower.includes("siki")) return "#f59e0b";
    return "#666";
  };

  return (
    <>
      <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
        <TableCell>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => setOpen(!open)}
          >
            {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </TableCell>
        <TableCell>{new Date(payment.createdDate || payment.purchaseDate).toLocaleDateString()}</TableCell>
        <TableCell>
          <Chip
            label={getPaymentMethodDisplay(payment)}
            size="small"
            sx={{
              backgroundColor: getPaymentMethodColor(getPaymentMethodDisplay(payment)),
              color: '#fff',
              fontWeight: 'bold'
            }}
          />
        </TableCell>
        <TableCell>${payment.amount || payment.subscriptionFee || '0'}</TableCell>
        <TableCell>
          <Chip
            label={payment.isActive ? "Active" : "Expired"}
            size="small"
            color={payment.isActive ? "success" : "default"}
          />
        </TableCell>
        <TableCell>{payment.type || 'subscription'}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 1 }}>
              <Typography variant="h6" gutterBottom component="div">
                Transaction Details
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">Transaction ID</Typography>
                  <Typography variant="body2" gutterBottom>
                    {payment.transactionId || payment._id || 'N/A'}
                  </Typography>
                  {payment.orderId && (
                    <>
                      <Typography variant="body2" color="text.secondary">Order ID</Typography>
                      <Typography variant="body2" gutterBottom>
                        {payment.orderId}
                      </Typography>
                    </>
                  )}
                  {payment.productId && (
                    <>
                      <Typography variant="body2" color="text.secondary">Product ID</Typography>
                      <Typography variant="body2" gutterBottom>
                        {payment.productId}
                      </Typography>
                    </>
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">Start Date</Typography>
                  <Typography variant="body2" gutterBottom>
                    {new Date(payment.startDate || payment.purchaseDate || payment.createdDate).toLocaleString()}
                  </Typography>
                  {payment.endDate && (
                    <>
                      <Typography variant="body2" color="text.secondary">End Date</Typography>
                      <Typography variant="body2" gutterBottom>
                        {new Date(payment.endDate || payment.expiresDate).toLocaleString()}
                      </Typography>
                    </>
                  )}
                  <Typography variant="body2" color="text.secondary">Auto Renewal</Typography>
                  <Typography variant="body2" gutterBottom>
                    {payment.autoRenewal ? 'Enabled' : 'Disabled'}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

export default function PremiumPaymentHistory({ userId, onClose }) {
  const [loading, setLoading] = useState(true);
  const [payments, setPayments] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (userId) {
      fetchPaymentHistory();
    }
  }, [userId]);

  const fetchPaymentHistory = async () => {
    setLoading(true);
    setError(null);
    try {
      // For now, we'll display the payment method information from the user object
      // In a real implementation, you would fetch transaction history from appropriate endpoints
      
      // Mock data based on available user information
      const paymentInfo = [];
      
      // Check if user has premium
      if (userId) {
        // Get user details to infer payment method
        const userResponse = await ApiService.ADMIN_GET_USER_STATS(userId);
        const userData = userResponse?.result || {};
        
        // Create a payment entry based on available data
        if (userData.hasPremium || userData.subscriptionStatus) {
          const paymentEntry = {
            _id: userId + '_premium',
            createdDate: userData.createdAt || new Date(),
            subscriptionStatus: userData.subscriptionStatus || 'active',
            subscriptionFee: userData.subscriptionFee || 0,
            deviceType: userData.deviceType,
            autoRenewal: userData.autoRenewal,
            isActive: true,
            type: 'subscription',
            // Infer payment method from device type
            paymentMethod: userData.deviceType === 2 ? 'iOS' : userData.deviceType === 1 ? 'Android' : 'Web',
            moonPayCustomerId: userData.moonPayCustomerId
          };
          paymentInfo.push(paymentEntry);
        }
      }

      setPayments(paymentInfo);
      
      // Note: In production, you would implement proper API endpoints to fetch:
      // 1. Subscription history with payment methods
      // 2. Transaction history filtered by subscription type
      // 3. In-app purchase records
      
    } catch (error) {
      console.error("Failed to fetch payment history:", error);
      setError("Payment history not available. The system needs to be updated to track payment methods for subscriptions.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper
      elevation={3}
      sx={{
        p: 3,
        backgroundColor: '#1f2937',
        border: '1px solid #f59e0b',
        maxHeight: '80vh',
        overflow: 'auto'
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h6" sx={{ color: '#f59e0b' }}>
          Premium Payment History
        </Typography>
        <Button onClick={onClose} variant="outlined" size="small">
          Close
        </Button>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress sx={{ color: '#f59e0b' }} />
        </Box>
      ) : error ? (
        <Typography color="error">{error}</Typography>
      ) : payments.length === 0 ? (
        <Typography>No payment history found</Typography>
      ) : (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell>Date</TableCell>
                <TableCell>Payment Method</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Type</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {payments.map((payment, index) => (
                <PaymentRow key={payment._id || index} payment={payment} />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
}